import { FC, useMemo } from 'react'
import { ButtonTransformer } from 'src/relay/Transformers/ButtonTransormer'
import Talents, { TalentsProps } from 'src/components/Talents'
import { ImageTransformer } from 'src/relay/Transformers/ImageTransformer'
import { getThemeColor } from 'src/relay/Transformers/ThemeTransformer'
import Router from 'src/routes/Router'
import routes from 'src/routes/definitions'
import PortraitGrid, { PortraitGridProps } from 'src/components/PortraitGrid'
import { RichTextTransformer } from 'src/relay/Transformers/RichTextTransformer'

import { BlockProps } from '../props'
import { ActionIconBackground } from '../../../components/ActionButton/types'
import { colors } from '../../../theme'

import { TalentsBlockProps } from './props'

const TalentsBlock: FC<BlockProps<TalentsBlockProps>> = ({
  data,
  locale,
  configuration,
}) => {
  const props: TalentsProps | PortraitGridProps = useMemo(
    () => ({
      ...(data.title && {
        blockTitleProps: {
          title: data.title,
          ...(data.description && {
            richTextProps: RichTextTransformer(
              data.description,
              configuration,
              locale
            ),
          }),
          actionButtonProps: ButtonTransformer({
            data: data.cta,
            theme: data.theme?.data,
            forceColor: colors.blueLight,
            iconBackground: ActionIconBackground.color,
          }),
          color: getThemeColor(data.theme?.data),
        },
      }),
      color: getThemeColor(data.theme?.data) ?? colors.blueLight,
      // v4 wraps as `talents.data[]`; v5 returns a flat array.
      portraits: ((data.talents as any)?.data ?? data.talents ?? []).map(
        (talentRaw: any) => {
          const attrs: any = talentRaw?.attributes ?? talentRaw
          const desktopImage =
            attrs?.hero?.desktopImage?.data ?? attrs?.hero?.desktopImage
          const mobileImage =
            attrs?.hero?.mobileImage?.data ?? attrs?.hero?.mobileImage
          const imageProps = ImageTransformer(desktopImage, {
            tablet: '100vw',
            big: '50vw, 33vw',
          })
          const mobileImageProps = ImageTransformer(mobileImage, {
            tablet: '100vw',
            big: '50vw, 33vw',
          })
          return {
            ...(imageProps && {
              pictureProps: {
                imageProps,
                ...(mobileImageProps && { mobileImageProps }),
              },
            }),
            ...(attrs?.slug && {
              linkProps: {
                href: Router.getRouteUrl(routes.talents, { slug: attrs.slug }),
                locale,
              },
            }),
            ...(attrs?.name && { name: attrs.name }),
            ...(attrs?.profession && { status: attrs.profession }),
            color: getThemeColor(attrs?.theme),
          }
        }
      ),
    }),
    [
      configuration,
      data.cta,
      data.description,
      data.talents,
      data.theme,
      data.title,
      locale,
    ]
  )

  return !data?.mosaique ? (
    <Talents {...(props as TalentsProps)} />
  ) : (
    <PortraitGrid {...props} />
  )
}

export default TalentsBlock
